import type { Notice } from "../../services/api/types";
import type { DriveItem } from "../../hooks/drive/driveTypes";
import { buildFolderSelectionKey, getDriveFolderKey, ORG_SHARE_TARGET } from "../../utils/driveFolder";

/** Whether a Notice is in the Circle Drive's Circle filter picks ("all" for every Circle). */
export const isInDriveCircle = (notice: Notice, circleId: string | undefined) =>
  !circleId || circleId === "all"
    ? true
    : notice.targetSubcircle
      ? notice.targetSubcircle === circleId
      : circleId === ORG_SHARE_TARGET;

/**
 * The files Drive shows: each sent Notice's attachments, newest first, in
 * folders or at Drive's root. `circleId` narrows them to one Circle's, as
 * Drive's Circle filter does ("all" for every Circle). Notices in
 * `hiddenNoticeIds` are left out (certificates, shown in their own folder).
 */
export function driveFiles(
  notices: Notice[] | undefined,
  schoolId: string | undefined,
  circleId = "all",
  hiddenNoticeIds?: Set<string>,
): DriveItem[] {
  const items: DriveItem[] = [];
  const circleNotices = (notices ?? []).filter((notice) => isInDriveCircle(notice, circleId));

  circleNotices.forEach((notice) => {
    if (notice.isDeleted || notice.isDraft) return;
    if (!notice.attachments?.length) return;
    if (hiddenNoticeIds?.has(notice.noticeId || notice.id || "")) return;

    const isBacking = getDriveFolderKey(notice.title, undefined) != null;
    const filedToFolder = Boolean(notice.driveFolderKey?.trim());

    notice.attachments.forEach((attachment) => {
      // Filed copies live on the backing notice; skip originals to avoid duplicates.
      if (!isBacking && filedToFolder) return;
      // Folder-scoped (path prefix or backing notice title), or at Drive root.
      const inFolder = getDriveFolderKey(notice.title, attachment.name) != null;
      if (!inFolder && isBacking) return;
      items.push({
        attachment,
        noticeId: notice.noticeId || notice.id,
        noticeTitle: notice.title,
        circleId: notice.targetSubcircle || schoolId || "",
        createdAt: notice.createdAt,
      });
    });
  });

  return items.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/** Who a file is shared with, as Drive groups its folders: a Circle, or the whole Organisation. */
export const driveAudienceOf = (file: DriveItem, schoolId: string | undefined) =>
  file.circleId && file.circleId !== schoolId ? file.circleId : ORG_SHARE_TARGET;

/** The folder Drive opens to show a file, or undefined for a file at Drive's root. */
export function driveFolderOf(file: DriveItem, schoolId: string | undefined): string | undefined {
  const folderName = getDriveFolderKey(file.noticeTitle, file.attachment.name);
  return folderName ? buildFolderSelectionKey(folderName, driveAudienceOf(file, schoolId)) : undefined;
}

export type DriveCircleFolder = { circleId: string; name: string; fileCount: number };

/**
 * Drive's root: one folder per Circle, in the order given (the Organisation's
 * own circle first), with how many files are shared with it. A Circle with no
 * files still gets its folder.
 */
export function circleFolders(
  circles: Array<{ circleId: string; name: string }>,
  notices: Notice[] | undefined,
  schoolId: string | undefined,
  hiddenNoticeIds?: Set<string>,
): DriveCircleFolder[] {
  const counts = new Map<string, number>();
  driveFiles(notices, schoolId, "all", hiddenNoticeIds).forEach((file) => {
    const audience = driveAudienceOf(file, schoolId);
    counts.set(audience, (counts.get(audience) ?? 0) + 1);
  });
  return circles.map(({ circleId, name }) => ({ circleId, name, fileCount: counts.get(circleId) ?? 0 }));
}
